/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { BCode } from '@src/app/model/bcode';
import { Cycle } from '@src/app/model/cycle';
import { Field } from '@src/app/shared/field/Field';
import { Schedule } from '@src/app/model/schedule';
import { Txn } from '@src/app/model/txn';
import { Unit } from '@src/app/model/unit';
import { PersonService } from '@src/app/pages/person-page/person.service';
import { AbstractPageComponent } from '@src/app/shared/form/abstract-page.component';
import { FieldSet } from '@src/app/shared/form/field-set/field-set.component';
import { FormComboBoxComponent } from '@src/app/shared/form/form-combo-box/form-combo-box.component';
import { FormDateComponent } from '@src/app/shared/form/form-date/form-date.component';
import { FormNumberComponent } from '@src/app/shared/form/form-number/form-number.component';
import { AppPicklistControl, FormPicklistComponent } from '@src/app/shared/form/form-picklist/form-picklist.component';
import { FormTextAreaComponent } from '@src/app/shared/form/form-text-area/form-text-area.component';
import { FormTextComponent } from '@src/app/shared/form/form-text/form-text.component';
import { FormConfig } from "@src/app/shared/form/FormConfig";
import { GridField } from '@src/app/shared/grid/grid-field';
import { GridControl } from '@src/app/shared/grid/grid-control';
import { validateTxnDate, required, validateDate, nonZero } from '@src/app/shared/validators';
import { BCodeService } from '@src/app/modules/budget/bcode.service';
import { CycleService } from '@src/app/modules/budget/cycle.service';
import { PeriodService } from '@src/app/modules/budget/period.service';
import { ScheduleService } from '@src/app/modules/budget/schedule.service';
import { UnitService } from '@src/app/modules/unit/unit.service';
import { CurrentUserService } from '@src/app/modules/user/current-user.service';
import { CreditNoteService } from '@src/app/modules/txn/credit-note.service';
import { PicklistField } from '@src/app/shared/field/PicklistField';
import { FieldMaker } from '@src/app/shared/field/FieldMaker';
import { FormPageComponent } from '@src/app/shared/form/form-page/form-page.component';
import { FormNavLinkComponent } from '@src/app/shared/form/form-nav-button/form-nav-link.component';
import { PersonPageNavRoute } from '@src/app/routes/person-page-nav';
import { InvoicePageNavRoute } from '@src/app/routes/invoice-page-nav';
import { CreditNotePageNavRoute } from '@src/app/routes/credit-note-page.nav';
import { OmcSettingsService } from '@src/app/pages/omc-settings/omc-settings.service';
import { OmcSettings } from '@src/app/model/OmcSettings';
import { concatMap, first, map } from 'rxjs/operators';
import { VatRateService } from '@src/app/modules/txn/VATRate.service';
import { uuid } from '@src/app/model/abstract-object';
import { AppFormControl } from '@src/app/shared/form/app-form-control';
import { MailService } from '@src/app/shared/mail-service';
import { AttachmentGridComponent } from '@src/app/shared/form/file/attachment-grid/attachment-grid.component';

@Component({
    selector: 'app-credit-note-page',
    templateUrl: './credit-note-page.component.html',
    styleUrls: ['./credit-note-page.component.scss'],
    imports: [FormPageComponent]
})
export class CreditNotePageComponent extends AbstractPageComponent {
    dataSvc = inject(CreditNoteService);
    bCodeSvc = inject(BCodeService);
    protected activeRoute = inject(ActivatedRoute);
    protected unitSvc = inject(UnitService);
    personSvc = inject(PersonService);
    protected cycleSvc = inject(CycleService);
    protected periodSvc = inject(PeriodService);
    private mailSvc = inject(MailService);
    protected scheduleSvc = inject(ScheduleService);
    private omcSettingsSvc = inject(OmcSettingsService);
    private vatRateSvc = inject(VatRateService);
    private currentUserSvc = inject(CurrentUserService);


    readonly path = Txn.TYPE.CREDIT_NOTE.code

    vatRegistered = false;
    vatControlAccountId: uuid;

    cycles: Cycle[] = [];
    bCodes: BCode[] = [];
    lineItemBCodes: BCode[] = [];

    // Dunno why it needs that dumb refresh periodId
    cycleField: PicklistField = FormPicklistComponent.make('Cycle', 'txnCycleId', 'txnCycle',
        { items: this.cycles, refreshes: ['periodId'] }, { formColumn: 2, visible: Field.noShow }
    );

    periodField: PicklistField = FormPicklistComponent.make('Period', 'txnPeriodId', 'txnPeriod',
        { items: [] },
        {
            formColumn: 2, visible: Field.noShow,
            refresh: (o: Cycle, control: AppPicklistControl) => {
                if (o) {
                    control.field.setPicklistItems(o.periods);
                    control.setValue(null);
                }
            },
        }
    );

    txnDateField: Field = FormDateComponent.make('Transaction Date', 'txnDate', {
        cellOpts: { width: '2%' }, formColumn: 3,
        validators: [validateTxnDate(this.cycleField, this.periodField)],
        valueChanges: this.dateValueChanges.bind(this)
    });

    schedules: Schedule[] = [];

    childTxn = new GridField({
        field:
            { label: $localize`Credit Details`, value: 'details', visible: Field.formOnly, formRow: 2 },
        rowFactory: this.childRowFactory.bind(this),
        objFactory: this.newChildTxn.bind(this),
    });

    creditField = FormNumberComponent.make("Total", "credit",
        { format: 'currency', width: 9, formatParms: '1.2-2' },
        { formRow: 1, formColumn: 4, readonly: true, validators: [nonZero] }
    );

    idField = FieldMaker.id();
    refField = FormNumberComponent.make('Reference', 'refNr', { format: 'plain' }, { cellOpts: { heading: 'Ref' }, readonly: true, formColumn: 3 });

    revField = FieldMaker.rev({ visible: Field.showAll, readonly: true, formColumn: 3 });

    outstandingField = FormNumberComponent.make("Outstanding", "outstanding",
        { format: 'currency', width: 9, formatParms: '1.2-2' },
        { formRow: 1, formColumn: 4, readonly: true, disable: true }
    );

    vatTotalField = FormNumberComponent.makeCurrency("VAT", "vatAmount",
        { formRow: 1, formColumn: 4, readonly: true, sendServer: false, visible: Field.noShow}
    );    

    personIdField = FieldMaker.idHolder('personId');
    personNameField = FormNavLinkComponent.makeDetail('Contact', 
        (o: Txn) => o.person?.fullName, 'personId', 
        PersonPageNavRoute ).override({formColumn: 2}
    );
    addressField = FormTextAreaComponent.make('Address', 'unit.address', {
        name: 'Address', formColumn: 2, readonly: true, disable: true, sendServer: false,
    });

    mailItemGrid = this.mailSvc.getMailItemGrid(this.dataSvc, 'model');

    attachmentGrid = AttachmentGridComponent.make(this.dataSvc, { readonly: true});    

    config = new FormConfig({
        navRoute: CreditNotePageNavRoute,
        docPath: 'omc-books/service-charges/credit-notes',
        title: Txn.TYPE.CREDIT_NOTE.name,
        help: $localize`Service Credit Notes`,
        readonly: true,
        fieldSet: new FieldSet({
            fields: [

                this.idField,
                this.refField,

                FormTextComponent.make('ledgerId', 'ledgerId', { visible: Field.noShow }),
                FormTextComponent.make('typeId', 'typeId', { visible: Field.noShow }),
                FormTextComponent.make('bCodeId', 'bCodeId', { visible: Field.noShow }),

                /* txtDateField Validator overwrites period info, need this order! */
                this.cycleField,
                this.periodField,
                this.txnDateField,
                this.revField,                

                FormComboBoxComponent.make('Unit', 'unitId', 'unit',
                    {
                        service: this.unitSvc, optionDisplayValue: (o: Unit) => Unit.getFullName(o),
                        refreshes: [this.refreshUnit.bind(this)]
                    },
                    { formColumn: 1, validators: [required] },
                ),
                FormDateComponent.make('Services From', 'svcFrom', {
                    cellOpts: { width: '2%' }, formColumn: 1,
                    validators: [validateDate]
                }),
                FormDateComponent.make('Services To', 'svcTo', {
                    cellOpts: { width: '2%' }, formColumn: 1,
                    validators: [validateDate]
                }),                
                this.personIdField, this.personNameField, this.addressField,
                FormTextAreaComponent.make('Notes', 'notes', {
                    name: 'notes', formColumn: 5, visible: Field.formOnly,
                    hint: $localize`Optional notes for this charge that appear on statement and credit note`,
                }),
                this.creditField,
                this.vatTotalField,
                this.outstandingField,
                this.childTxn,
            ],
            formLayout: [
                { cells: [{ width: '20%' }, { width: '20%' }, { width: '20%' }, { width: '5%' }, { width: '20%' }] },
                { cells: [{ colspan: 4, pageTab: 'yes' }] }
            ],
        }),
        service: this.dataSvc,
        mode: 'list',
        objectFactory: this.newFactory.bind(this),
        configReady: this.configReady,
        beforeEdit: this.beforeEdit.bind(this),
        newOptions: [
            { name: $localize`New ${Txn.TYPE.INVOICE.name}`, basePath: InvoicePageNavRoute.newUrl, id: 'newInvoice' },
            { name: $localize`New ${Txn.TYPE.CREDIT_NOTE.name}`, basePath: CreditNotePageNavRoute.newUrl, params: { typeId: Txn.TYPE.CREDIT_NOTE.id }, id: 'newCreditNote' }
        ],
        tabFields: [this.attachmentGrid, this.mailItemGrid]
    });

    constructor() {
        super();
        const vatRateSvc = this.vatRateSvc;
        const currentUserSvc = this.currentUserSvc;


        forkJoin({
            currentUser: currentUserSvc.getCurrentUser().pipe(first()),
            schedules: this.scheduleSvc.get(true).pipe(first()), bcodes: this.bCodeSvc.get<BCode>(),
            cycles: this.cycleSvc.get<Cycle>(true), defaultBCodes: this.currentUserSvc.getDefaultBCodes().pipe(first()),
            defBCodes: this.currentUserSvc.getDefaultBCodes().pipe(first()),            
            omcSettings: this.omcSettingsSvc.getOne<OmcSettings>('omcSettings', null).pipe( concatMap(
                omcSettings => {
                    if (vatRateSvc.vatRegistered(omcSettings)) {
                        return this.vatRateSvc.getVatRates().pipe( map( () => omcSettings))
                    }
                    return of(omcSettings)
                }
            ))
        }).subscribe(
            (result) => {
                this.currentUser = result.currentUser;
                this.schedules = result.schedules as Schedule[];
                this.currentCycle = this.cycleSvc.getCurrentCycle(result.cycles);
                this.cycleField.setPicklistItems(result.cycles);
                this.bCodes = result.bcodes;
                this.lineItemBCodes.length = 0;
                this.bCodes.forEach( bc => {
                    if (bc.typeId === BCode.TYPE.INCOME.id || bc.typeId === BCode.TYPE.EXPENSE.id || bc.typeId === BCode.TYPE.CAPITAL.id) {
                        if (!result.defBCodes.find( dbc => dbc.bCodeId === bc.id)) {
                            this.lineItemBCodes.push(bc);
                        }
                    }
                });
                if (this.vatRateSvc.vatRegistered(result.omcSettings)) {
                    this.vatRegistered = true;
                    this.vatTotalField.visible = Field.formOnly;
                } else {
                    this.vatTotalField.visible = Field.noShow;
                }
                this.vatControlAccountId = this.currentUserSvc.getDefaultBCode('VatControl')?.bCodeId;

                this.configReady.next(null);    
            });
    }

    refreshUnit(u: Unit) {
        this.personIdField.control.setValue(u.ownerId, {emitEvent: false});
        this.personNameField.control.setValue(u.owner.fullName);
        this.addressField.control.setValue(u.owner?.address ? u.owner.address : u.address);
    }


    beforeEdit(o: Txn) {
        this.config.readonly = true; 
        if (this.vatRegistered) {
            const vatTxn = o.details.find( t => t.typeId === Txn.TYPE.CHARGES_VAT.id);
            o.details = o.details.filter( t => t.typeId !== Txn.TYPE.CHARGES_VAT.id);
            if (vatTxn) {
                o.vatAmount = vatTxn.debit - vatTxn.credit;
            } else {
                console.log('No Vat Control');
            }
            
        }        
        this.refField.visible = Field.showAll;
        return o;
    }

    newFactory(): Observable<Txn> {
        this.refField.visible = Field.noShow;
        this.revField.visible = Field.noShow;

        const txn = new Txn();
        this.config.readonly = false;

        txn.ledgerId = Txn.LEDGER.AR.id;
        txn.typeId = Txn.TYPE.CREDIT_NOTE.id;

        //txn.bCode = this.currentUser.currentTeam.debtorsBCode;
        txn.bCodeId = this.currentUserSvc.getDefaultBCode('Debtors').bCodeId;

        //txn.txnDate = moment().toISOString().substring(0, 10);
        //txn.txnCycleId = this.cycleSvc.getCurrentCycle().id;
        //txn.txnPeriodId = this.cycleSvc.getCurrentPeriod().id;
        //const cycle = this.cycleSvc.getCurrentCycle(this.cycles);
        //txn.svcFrom = cycle.from;
        //txn.svcTo = cycle.to;

        // txn.led
        // txn.typeId = Txn.TYPE.INVOICE.id;
        // txn.bCodeId = Defaulted on the server?
        return of(txn);
    }
    
    childRowFactory() {
        const vatFields = [];
        if (this.vatRegistered) {
            vatFields.push(FormTextComponent.make('VAT Code', 'vatCode', {readonly: true}))
            vatFields.push(FormNumberComponent.make('VAT Rate', 'vatRate', {format: 'decimal', formatParms: '2.2-2'}, {readonly: true}))
            vatFields.push(FormNumberComponent.makeCurrency('VAT', 'vatAmount', {readonly: true}))
        }

        const bCodeField = FormPicklistComponent.make('Account', 'bCodeId', 'bCode',
                { items: this.lineItemBCodes},
                { cellOpts: { width: '30%' }, validators: [required] }
        );

        return [
            FieldMaker.id(),
            FormTextComponent.make('ledgerId', 'ledgerId', { visible: Field.noShow }),
            FormTextComponent.make('typeId', 'typeId', { visible: Field.noShow }),
            FormTextComponent.make('txnDate', 'txnDate', { visible: Field.noShow }),
            FormTextComponent.make('txnCycleId', 'txnCycleId', { visible: Field.noShow }),
            FormTextComponent.make('txnPeriodId', 'txnPeriodId', { visible: Field.noShow }),
            bCodeField,
            FormPicklistComponent.make('Schedule', 'scheduleId', 'schedule',
                { items: this.schedules },
                { cellOpts: { width: '30%' } }),

            FormNumberComponent.make("Amount", "debit",
                { format: 'currency', width: 9, formatParms: '1.2-2' },
                { valueChanges: this.creditValueChanges.bind(this) }
            ),
            ... vatFields,
            FieldMaker.notes({ cellOpts: { width: '30%' } }),
            FieldMaker.rev(),
            FieldMaker.deleteGridRow(),
        ];  
    }

    createChildItem(): Txn {
        const txn = new Txn();
        txn.ledgerId = Txn.LEDGER.AR.id;
        txn.typeId = Txn.TYPE.CREDIT_NOTE_ITEM.id;
        txn.txnDate = this.txnDateField.control.value;
        txn.txnPeriodId = this.periodField.control.value;
        txn.txnCycleId = this.cycleField.control.value;
        if (this.schedules.length === 1) {
            txn.scheduleId = this.schedules[0].id;
        }
        return txn;
    }

    newChildTxn(): Observable<Txn> {

        return of(this.createChildItem());
    }

    dateValueChanges(): void {
        const allocations = (this.childTxn.control as GridControl).controls;
        if (allocations) {
            for (const txnRow of allocations) {
                txnRow.get('txnDate').setValue(this.txnDateField.control.value, { emitEvent: false });
                txnRow.get('txnCycleId').setValue(this.cycleField.control.value, { emitEvent: false });
                txnRow.get('txnPeriodId').setValue(this.periodField.control.value, { emitEvent: false });
            }
        }
        this.creditValueChanges();
    }

    createVatItem() {
        const txn = this.createChildItem();
        txn.typeId = Txn.TYPE.CHARGES_VAT.id;
        txn.bCodeId = this.vatControlAccountId;
        return txn;
    }    

    creditValueChanges(): void {
        const txns = (this.childTxn.control as GridControl).controls;
        let total = 0;

        const txnDate = this.txnDateField.getFormValue();
        let vatTotal = 0;
        if (txns) {
            for (const tRow of txns) {
                const amt = tRow.get('debit').value;

                if (this.vatRegistered) {
                    const acctId = ((tRow.get('bCodeId') as AppFormControl).value);
                    const bc = this.bCodes.find( ec => ec.id === acctId);

                    tRow.get('vatCode').setValue(bc.vatCode, {emitEvent: false});

                    if (txnDate) {
                        const rate = this.vatRateSvc.getRate(bc.vatCode, txnDate);
                        const vatAmount = Math.round(amt * rate) / 100

                    
                        tRow.get('vatRate').setValue(rate, {emitEvent: false});
                        tRow.get('vatAmount').setValue(0 - vatAmount, {emitEvent: false});

                        total += vatAmount;
                        vatTotal += vatAmount;                    
                    }
                    console.log({total, vatTotal});
                }
            
                total += amt;
            }
        }
        
        this.creditField.control.setValue(total);
        this.outstandingField.control.setValue(0 - total);
        this.vatTotalField.control.setValue(vatTotal);
     
    }
}
